import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BionicText } from '../components/custom/BionicText';
import { RecipeCard } from '../components/custom/RecipeCard';
import { NutritionPanel, NutritionGuide, MealNutrition } from '../components/custom/NutritionPanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Loader2,
  Sparkles,
  Download,
  Clock,
  ChefHat,
  Pill
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MealPlan() {
  const { planId } = useParams();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedDay, setSelectedDay] = useState(1);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (token && planId) {
      fetchPlan();
    }
  }, [token, planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/diet-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlan(response.data);
      
      // If no meal plan yet, generate one
      if (!response.data.meal_plan) {
        generateMealPlan();
      }
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      toast.error('Failed to load diet plan');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const generateMealPlan = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/diet-plans/${planId}/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlan(response.data);
      toast.success('Meal plan generated!');
    } catch (error) {
      console.error('Failed to generate meal plan:', error);
      toast.error('Failed to generate meal plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async (day, mealType, recipe) => {
    try {
      const response = await fetch(
        `${API_URL}/api/diet-plans/${planId}/recipe-pdf/${day}/${mealType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${recipe.name.replace(/\s+/g, '_')}_recipe.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Recipe PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (generating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
        <div className="text-center">
          <Sparkles className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
            Generating Your Meal Plan
          </h2>
          <p className="text-muted-foreground">
            Our AI is creating personalized recipes just for you...
          </p>
        </div>
        <Loader2 className="h-8 w-8 animate-spin text-primary mt-4" />
      </div>
    );
  }

  const mealPlan = plan?.meal_plan;
  const days = mealPlan?.days || [];
  const currentDay = days.find(d => d.day === selectedDay) || days[0];
  const nutritionGuide = mealPlan?.nutrition_guide;

  return (
    <main className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 opacity-0 animate-fade-in">
          <div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="mb-2 -ml-2"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back to Dashboard
            </Button>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              {plan?.name}
            </h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {plan?.diet_types.map((diet) => (
                <Badge key={diet} variant="secondary">{diet}</Badge>
              ))}
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={generateMealPlan}
              className="rounded-full gap-2"
              data-testid="regenerate-btn"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Regenerate
            </Button>
            <Button
              onClick={() => navigate(`/grocery-list/${planId}`)}
              className="rounded-full gap-2"
              data-testid="view-grocery-list-btn"
            >
              <ShoppingCart className="h-4 w-4" aria-hidden="true" />
              Grocery List
            </Button>
          </div>
        </div>

        {/* Nutrition Guide */}
        {nutritionGuide && (
          <div className="mb-8 opacity-0 animate-fade-in stagger-1">
            <NutritionGuide guide={nutritionGuide} />
          </div>
        )}

        {/* Day Tabs */}
        {days.length > 0 && (
          <Tabs 
            value={selectedDay.toString()} 
            onValueChange={(v) => setSelectedDay(parseInt(v))}
            className="opacity-0 animate-fade-in stagger-2"
          >
            <TabsList className="mb-6 flex-wrap h-auto gap-2 bg-transparent">
              {days.map((day) => (
                <TabsTrigger
                  key={day.day}
                  value={day.day.toString()}
                  className="rounded-full px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid={`day-tab-${day.day}`}
                >
                  Day {day.day}
                </TabsTrigger>
              ))}
            </TabsList>

            {days.map((day) => (
              <TabsContent key={day.day} value={day.day.toString()}>
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Meals Column */}
                  <div className="lg:col-span-2 space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => {
                        const meal = day.meals?.[mealType];
                        if (!meal) return null;
                        
                        return (
                          <RecipeCard
                            key={mealType}
                            recipe={meal}
                            planId={planId}
                            day={day.day}
                            mealType={mealType}
                            onViewDetails={() => setSelectedRecipe({ ...meal, day: day.day, mealType })}
                            onDownloadPdf={() => handleDownloadPdf(day.day, mealType, meal)}
                          />
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Daily Nutrition Panel */}
                  <div className="space-y-4">
                    {day.daily_totals && (
                      <NutritionPanel dailyTotals={day.daily_totals} />
                    )}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Recipe Detail Dialog */}
        <Dialog open={!!selectedRecipe} onOpenChange={() => setSelectedRecipe(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="recipe-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {selectedRecipe?.name}
              </DialogTitle>
            </DialogHeader>
            
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-6 pr-4">
                <p className="text-muted-foreground">{selectedRecipe?.description}</p>
                
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <span>Prep: {selectedRecipe?.prep_time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ChefHat className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <span>Cook: {selectedRecipe?.cook_time}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-3">Ingredients</h4>
                  <ul className="space-y-2">
                    {selectedRecipe?.ingredients?.map((ing, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 rounded-full bg-primary" aria-hidden="true" />
                        <span>{ing.quantity} {ing.unit} {ing.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-3">Instructions</h4>
                  <ol className="space-y-3">
                    {selectedRecipe?.instructions?.map((step, idx) => (
                      <li key={idx} className="flex gap-3 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                          {idx + 1}
                        </span>
                        <span className="text-muted-foreground">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </ScrollArea>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setSelectedRecipe(null)}
                className="rounded-full"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  if (selectedRecipe) {
                    handleDownloadPdf(selectedRecipe.day, selectedRecipe.mealType, selectedRecipe);
                  }
                }}
                className="rounded-full gap-2"
                data-testid="download-recipe-pdf-btn"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
